<script lang="ts">
import type { Component } from 'vue'

export interface ListItem {
  as?: string | Component
  label?: string
  helperText?: string
  icon?: string
  trailingIcon?: string
  color?: 'primary' | 'critical'
  type?: 'label' | 'separator' | 'link'
  loading?: boolean
  disabled?: boolean
  onClick?: (e: Event) => void
  class?: string | object
  textClass?: string | object
}
interface ListItemProps extends ListItem {
  children?: ArrayOrNested<ListItem>
}

export const ui = {
  separator: 'flex h-px bg-secondary-200',
  itemLeadingIcon: 'shrink-0',
  itemTrailing: 'ms-auto inline-flex gap-1.5 items-center',
  itemTrailingIcon: 'shrink-0',
  itemLabel: 'truncate',
  helperText: 'truncate text-secondary-800 text-sm font-400',
  root: 'w-full flex items-center p-1 text-md font-500 gap-1.5',
  base: 'transition-colors data-[disabled]:opacity-30 disabled:opacity-30 data-[disabled]:cursor-not-allowed disabled:cursor-not-allowed w-full flex items-center py-2 px-2.25 text-md font-500 gap-1.5 rounded-md bg-white data-[disabled]:hover:bg-white disabled:hover:bg-white',
  cursor: 'cursor-pointer',
  loading: 'animate-spin',
  color: {
    primary: 'text-secondary-900 hover:bg-primary-50',
    critical: 'text-critical-600 hover:bg-critical-50',
  },
  item: {
    root: 'flex gap-1.5 w-full',
    wrapper: 'flex flex-col',
    labelWrapper: 'flex items-center gap-1',
  },
}
</script>

<script setup lang="ts">
import type { ArrayOrNested } from '../types/utils'
import { createReusableTemplate } from '@vueuse/core'
import { DropdownMenu } from 'reka-ui/namespaced'
import { computed } from 'vue'
import icons from '../styles/icons'
import AwIcon from './AwIcon.vue'

const props = defineProps<ListItemProps>()

const [DefineItemTemplate, ReuseItemTemplate] = createReusableTemplate()

const childrenIcon = computed(() => icons.chevronRight)

const color = computed(() => props.color ?? 'primary')
</script>

<template>
  <DefineItemTemplate>
    <div :class="ui.item.root">
      <div :class="ui.item.wrapper">
        <div :class="ui.item.labelWrapper">
          <AwIcon v-if="loading" :name="icons.loading" :class="[ui.itemLeadingIcon, ui.loading]" />
          <AwIcon v-else-if="icon" :name="icon" :class="ui.itemLeadingIcon" />
          <span v-if="label" :class="[ui.itemLabel, textClass]">
            {{ label }}
          </span>
        </div>
        <span v-if="helperText" :class="ui.helperText">
          {{ helperText }}
        </span>
      </div>
      <span v-if="children?.length || trailingIcon" :class="ui.itemTrailing">
        <AwIcon :name="childrenIcon" :class="ui.itemTrailingIcon" />
      </span>
    </div>
  </DefineItemTemplate>
  <Component :is="as ?? 'span'" v-if="type === 'separator'" :class="ui.separator" />
  <div v-else-if="type === 'label' || children?.length" :class="ui.root">
    <Component :is="as ?? 'span'" v-if="type === 'label'" :class="ui.base">
      <ReuseItemTemplate />
    </Component>
    <DropdownMenu.SubTrigger
      v-else-if="children?.length"
      as="button"
      type="button"
      :disabled
      :text-value="label"
      :class="[ui.base, ui.color[color], ui.cursor]"
    >
      <ReuseItemTemplate />
    </DropdownMenu.SubTrigger>
  </div>
  <Component
    :is="as ?? 'span'"
    v-else
    as-child
    :disabled
    :text-value="label"
  >
    <div :class="ui.root">
      <button :disabled :class="[ui.base, ui.color[color]]" @click="onClick">
        <ReuseItemTemplate />
      </button>
    </div>
  </Component>
</template>
